import { Component, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle size={28} />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Något gick fel</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Ett oväntat fel inträffade. Försök ladda om sidan eller gå tillbaka till startsidan.
          </p>
          {this.state.error?.message && (
            <pre className="mt-4 text-left text-xs bg-muted text-muted-foreground p-3 rounded-lg overflow-auto max-h-32">
              {this.state.error.message}
            </pre>
          )}
          <div className="mt-6 flex gap-2 justify-center">
            <button
              onClick={() => { this.reset(); window.location.reload(); }}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition"
            >
              Ladda om
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition"
            >
              Till start
            </a>
          </div>
        </div>
      </div>
    );
  }
}
